import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ColumnFilterRow, applyColumnFilters } from "../../Components/Common/DashboardKit";
import useTableSort from "../../Components/Common/DashboardKit/useTableSort";
import SortableTh from "../../Components/Common/DashboardKit/SortableTh";
import {
    Container, Row, Col, Card, CardBody,
    Input, Button, Badge, Spinner,
    Pagination, PaginationItem, PaginationLink,
} from 'reactstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    DashboardPageHeader,
    DataTableShell,
} from '../../Components/Common/DashboardKit';
import api from '../../api';
import useAuth from '../Personal Attendance/useAuth';

/**
 * Employee Role Assignment (IT Admin only)
 *
 *   - Lists every employee with their current role.
 *   - Search across EmpID / FirstName / LastName / Name / Company / RoleName / RoleId.
 *   - Inline role dropdown + Save button per row.
 *   - "Unassigned" badge for NULL RoleId; can be cleared back to NULL.
 *
 * Backend endpoints:
 *   GET  /api/admin/roles
 *   GET  /api/admin/employees-with-roles
 *   PUT  /api/admin/employees/:empId/role   body: { roleId: number | null }
 *
 * The route is wrapped in <RoleProtected requireItAdmin> in allRoutes.js.
 * This page also performs a belt-and-suspenders client check and shows the
 * same denied state if a non-IT-Admin somehow reaches it.
 *
 * Pass 3 UI update (2026):
 *  - The hand-rolled "loading spinner block / error alert / empty state /
 *    table" inside CardBody has been replaced with the shared DataTableShell
 *    from the DashboardKit. The shell now handles:
 *      - the search input + the row count badge
 *      - the refresh button (calls loadAll)
 *      - the skeleton loading state
 *      - the "Couldn't load data" / Try-again error block
 *      - the "no records found" empty state
 *    The actual <table> body — with the inline role dropdown and Save
 *    button per row — is passed as children, so all per-row logic
 *    (pendingRole, savingId, dirty detection, handleSave) is unchanged.
 *
 *  - The access-denied early-return is unchanged (the route guard
 *    handles this normally; this is the belt-and-suspenders check).
 *  - loadAll, handleSave, the search filter useMemo, every useState
 *    hook, every API call, every payload shape, the toast calls, and
 *    the optimistic update / rollback logic are all unchanged.
 */
const EmployeeRoleAssignment = () => {
    const { isItAdmin } = useAuth();

    // ── data ──
    const [roles, setRoles] = useState([]);
    const [employees, setEmployees] = useState([]);

    // ── ui ──
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [search, setSearch] = useState('');

    // pending edits + per-row saving state, keyed by empId
    const [pendingRole, setPendingRole] = useState({}); // { [empId]: roleIdString | "" }
    const [savingId, setSavingId] = useState(null);

    // ─────────────────────────────────────────────────────────────────────
    // Load roles + employees on mount.
    // We load both in parallel; either failing surfaces a single error banner
    // rather than leaving the page half-rendered.
    // ─────────────────────────────────────────────────────────────────────
    const loadAll = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            // REACT_APP_API_URL ends in "/api/", so paths are relative without leading "/api/".
            const [rolesRes, empsRes] = await Promise.all([
                api.get('admin/roles'),
                api.get('admin/employees-with-roles'),
            ]);
            setRoles(rolesRes.data || []);
            setEmployees(empsRes.data || []);
        } catch (err) {
            // 401/403 will land here if the JWT doesn't have IT Admin role.
            const status = err?.response?.status;
            const msg = status === 403 || status === 401
                ? 'You are not authorized to view this page.'
                : (err?.response?.data?.error || err?.message || 'Failed to load data.');
            setLoadError(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isItAdmin) loadAll();
        else setLoading(false);
    }, [isItAdmin, loadAll]);

    // ─────────────────────────────────────────────────────────────────────
    // Search filter — case-insensitive across the fields listed in the spec.
    // ─────────────────────────────────────────────────────────────────────
    // Round 34 — EPO-style per-column filters + click-to-sort (the shell's
    // free-text search is replaced by the filter row).
    const [columnFilters, setColumnFilters] = useState({});
    // Options for the Assign Role select filter: every role label + "Unassigned".
    // Values match exactly what the assignRole column's `get` returns, so the
    // exact-match select filter works.
    const assignRoleOptions = useMemo(
        () => ["Unassigned", ...roles.map((r) => r.roleName)],
        [roles]
    );
    const tblColumns = useMemo(() => [
        { id: "empId", label: "Emp ID", type: "text", get: (e) => String(e.empId ?? "") },
        { id: "name", label: "Name", type: "text", get: (e) => e.name || `${e.firstName ?? ""} ${e.lastName ?? ""}`.trim() },
        { id: "company", label: "Company", type: "text", get: (e) => e.company || "" },
        { id: "dept", label: "Dept", type: "text", get: (e) => e.dept || e.department || "" },
        { id: "status", label: "Status", type: "text", get: (e) => e.status || "" },
        { id: "role", label: "Current Role", type: "text", get: (e) => e.roleName || "" },
        // Assign Role — a SELECT filter so you can narrow to everyone (to be) set
        // to a given role, or "Unassigned". `get` returns the EFFECTIVE assigned
        // role label: the pending (unsaved) choice if you changed it, else the
        // current assignment. Options are built from the roles list below so the
        // exact-match select filter lines up with what `get` returns.
        { id: "assignRole", label: "Assign Role", type: "select",
          options: assignRoleOptions,
          get: (e) => {
            const pending = pendingRole[e.empId];
            const effId = pending !== undefined
                ? (pending === "" ? null : Number(pending))
                : (e.roleId ?? null);
            if (effId == null) return "Unassigned";
            const r = roles.find((x) => x.roleId === effId);
            return r ? r.roleName : `Role ${effId}`;
          } },
        { id: "act",   label: "Action", type: "none" },
    ], [roles, pendingRole, assignRoleOptions]);
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return employees;
        return employees.filter((e) => {
            const haystack = [
                e.empId, e.firstName, e.lastName, e.name, e.company,
                e.roleName, e.roleId == null ? 'unassigned' : String(e.roleId),
            ].filter(Boolean).join(' ').toLowerCase();
            return haystack.includes(q);
        });
    }, [employees, search]);
    const filteredBase = applyColumnFilters(filtered, tblColumns, columnFilters);
    const { sorted: view, sort, toggleSort } = useTableSort(filteredBase, tblColumns);

    // ── Client-side pagination (the whole employee list is loaded up front) ──
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const totalRows = view.length;
    const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
    // Keep the current page valid as filters/sort/page-size change.
    useEffect(() => { setPage(1); }, [columnFilters, search, pageSize]);
    const safePage = Math.min(page, totalPages);
    const pageStart = (safePage - 1) * pageSize;
    const pagedView = view.slice(pageStart, pageStart + pageSize);

    // ─────────────────────────────────────────────────────────────────────
    // Save handler — only updates RoleId, optimistically reflects new role,
    // rolls back on error.
    // ─────────────────────────────────────────────────────────────────────
    const handleSave = async (emp) => {
        const raw = pendingRole[emp.empId];
        // raw values: "" means clear (null), otherwise it's a string of an int.
        const newRoleId = raw === '' ? null : Number(raw);

        if (raw !== '' && Number.isNaN(newRoleId)) {
            toast.error('Invalid role selection.');
            return;
        }
        if (newRoleId === emp.roleId) {
            toast.info('No change.');
            return;
        }

        setSavingId(emp.empId);
        try {
            const res = await api.put(
                `admin/employees/${encodeURIComponent(emp.empId)}/role`,
                { roleId: newRoleId }
            );
            const updated = res.data;
            setEmployees((list) =>
                list.map((e) => e.empId === emp.empId
                    ? { ...e, roleId: updated.roleId ?? null, roleName: updated.roleName ?? null }
                    : e
                )
            );
            setPendingRole((p) => {
                const next = { ...p };
                delete next[emp.empId];
                return next;
            });
            toast.success(
                newRoleId == null
                    ? `Cleared role for ${emp.empId}.`
                    : `Updated ${emp.empId} to ${updated.roleName ?? 'role ' + newRoleId}.`
            );
        } catch (err) {
            const status = err?.response?.status;
            const errMsg = err?.response?.data?.error
                || (status === 403 ? 'Not authorized to update roles.' : 'Failed to update role.');
            toast.error(errMsg);
        } finally {
            setSavingId(null);
        }
    };

    // ─── early-out for non-IT-Admin (defensive; route guard usually handles this) ──
    if (!isItAdmin) {
        return (
            <div className="page-content">
                <Container fluid>
                    <Card>
                        <CardBody className="text-center p-5">
                            <i className="ri-shield-cross-line text-warning" style={{ fontSize: 48 }} />
                            <h5 className="mt-3">Access Denied</h5>
                            <p className="text-muted mb-0">
                                You must be an IT Admin to view this page.
                            </p>
                        </CardBody>
                    </Card>
                </Container>
            </div>
        );
    }

    // The actual <table> body. The DataTableShell renders the chrome
    // around this (search bar, refresh button, count badge, sticky header,
    // skeleton, empty, error states). When the shell's `loading` / `empty` /
    // `error` flags are active, this body is replaced by the shell's
    // canned states instead of being rendered.
    const tableBody = (
        <div className="table-responsive">
            <table className="table align-middle table-nowrap table-hover mb-0">
                <thead className="table-light">
                    <tr>
                        {tblColumns.slice(0, -1).map((c) => (
                            <SortableTh key={c.id} col={c} sort={sort} onToggle={toggleSort} />
                        ))}
                        <th className="text-end">Action</th>
                    </tr>
                    <ColumnFilterRow columns={tblColumns} filters={columnFilters} onChange={setColumnFilters} />
                </thead>
                <tbody>
                    {pagedView.map((e) => {
                        const pending = pendingRole[e.empId];
                        const selectValue =
                            pending !== undefined
                                ? pending
                                : (e.roleId == null ? '' : String(e.roleId));
                        const dirty =
                            pending !== undefined &&
                            Number(pending || NaN) !== (e.roleId ?? NaN) &&
                            !(pending === '' && e.roleId == null);
                        return (
                            <tr key={e.empId}>
                                <td><code>{e.empId}</code></td>
                                <td>
                                    {e.name
                                        || `${e.firstName ?? ''} ${e.lastName ?? ''}`.trim()
                                        || <span className="text-muted">—</span>}
                                </td>
                                <td>{e.company || <span className="text-muted">—</span>}</td>
                                <td>{e.jcDept || <span className="text-muted">—</span>}</td>
                                <td>
                                    {e.empStatus
                                        ? <Badge color="light" className="text-dark">{e.empStatus}</Badge>
                                        : <span className="text-muted">—</span>}
                                </td>
                                <td>
                                    {e.roleId == null
                                        ? <Badge color="warning">Unassigned</Badge>
                                        : <Badge color="info">{e.roleName ?? `Role ${e.roleId}`}</Badge>}
                                </td>
                                <td>
                                    <Input
                                        type="select"
                                        bsSize="sm"
                                        value={selectValue}
                                        onChange={(ev) =>
                                            setPendingRole((p) => ({
                                                ...p,
                                                [e.empId]: ev.target.value
                                            }))
                                        }
                                        disabled={savingId === e.empId}
                                    >
                                        <option value="">— Unassigned —</option>
                                        {roles.map((r) => (
                                            <option key={r.roleId} value={r.roleId}>
                                                {r.roleName} (#{r.roleId})
                                            </option>
                                        ))}
                                    </Input>
                                </td>
                                <td className="text-end">
                                    <Button
                                        color={dirty ? 'primary' : 'light'}
                                        size="sm"
                                        disabled={!dirty || savingId === e.empId}
                                        onClick={() => handleSave(e)}
                                    >
                                        {savingId === e.empId ? (
                                            <>
                                                <Spinner size="sm" /> Saving…
                                            </>
                                        ) : (
                                            <>
                                                <i className="ri-save-line align-bottom me-1"></i>
                                                Save
                                            </>
                                        )}
                                    </Button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {/* Pagination controls */}
            {totalRows > 0 && (
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-2 py-3 border-top">
                    <div className="text-muted small">
                        Showing {pageStart + 1}–{Math.min(pageStart + pageSize, totalRows)} of {totalRows}
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <span className="text-muted small">Rows per page</span>
                        <Input
                            type="select"
                            bsSize="sm"
                            style={{ width: 80 }}
                            value={pageSize}
                            onChange={(e) => setPageSize(Number(e.target.value))}
                        >
                            {[10, 25, 50, 100].map((n) => (
                                <option key={n} value={n}>{n}</option>
                            ))}
                        </Input>
                        <Pagination size="sm" className="mb-0">
                            <PaginationItem disabled={safePage <= 1}>
                                <PaginationLink first onClick={() => setPage(1)} />
                            </PaginationItem>
                            <PaginationItem disabled={safePage <= 1}>
                                <PaginationLink previous onClick={() => setPage(safePage - 1)} />
                            </PaginationItem>
                            <PaginationItem active>
                                <PaginationLink>{safePage} / {totalPages}</PaginationLink>
                            </PaginationItem>
                            <PaginationItem disabled={safePage >= totalPages}>
                                <PaginationLink next onClick={() => setPage(safePage + 1)} />
                            </PaginationItem>
                            <PaginationItem disabled={safePage >= totalPages}>
                                <PaginationLink last onClick={() => setPage(totalPages)} />
                            </PaginationItem>
                        </Pagination>
                    </div>
                </div>
            )}
        </div>
    );

    // ─────────────────────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────────────────────
    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-shield-user-line"
                    title="Employee Role Assignment"
                    subtitle="Assign system roles to employees"
                    tone="violet"
                    breadcrumb={[
                        { label: "IT Admin", to: "/admin" },
                        { label: "Employee Role Assignment" },
                    ]}
                />

                <Row>
                    <Col xs={12}>
                        <DataTableShell
                            title="Assign roles to employees"
                            subtitle="Only the role assignment is changed. No other employee fields are modified."
                            rowCount={filtered.length}
                            countLabel="employees"
                            searchPlaceholder="Search EmpID, name, company, role…"
                            onRefresh={loadAll}
                            refreshing={loading}
                            loading={loading && employees.length === 0}
                            error={loadError}
                            empty={!loading && !loadError && filtered.length === 0}
                            emptyIcon="ri-inbox-line"
                            emptyTitle={search ? 'No matches' : 'No employees found'}
                            emptyMessage={
                                search
                                    ? 'No employees match your search. Try a different EmpID, name, company, or role.'
                                    : 'There are no employees to display.'
                            }
                            skeletonColumns={8}
                        >
                            {tableBody}
                        </DataTableShell>
                    </Col>
                </Row>
            </Container>
            <ToastContainer closeButton={false} limit={3} />
        </div>
    );
};

export default EmployeeRoleAssignment;
